import {
  showTimeByDateType,
  newMathFormat,
  getCountryNameByCountryCode,
} from '@/pages/intelligence/utils/commonUtils';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import { getIfPercentage } from '@/pages/intelligence/utils/common';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import { PC_CONSOLE_COMBINE_ENTITY_TYPE } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import moment from 'moment';
import { DETAIL_PAGEID } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_config';
import {
  hasMultiValueMetricDataInRow,
  isMultiValueMetric,
} from '@intelligence/pages/TopCharts/utils/multiValueMetricUtils';

const deviceValueTextMap = {
  'microsoft store': 'Xbox',
  'nintendo eshop': 'Nintendo',
  'playstation store': 'PlayStation',
  PS4: 'PlayStation 4',
  ps4: 'PlayStation 4',
  PS5: 'PlayStation 5',
  ps5: 'PlayStation 5',
  switch: 'Nintendo Switch',
  Switch: 'Nintendo Switch',
  pc: 'PC',
} as any;

export const getLocalizedCountryChartName = (params: {
  regionsCountries: any[];
  countryCode?: string;
  fallbackName?: string;
  localizedOthersName?: string;
}) => {
  const {
    regionsCountries,
    countryCode,
    fallbackName = '',
    localizedOthersName = 'Others',
  } = params;
  const code = typeof countryCode === 'string' ? countryCode.trim() : '';
  const fallbackText = fallbackName || code;
  const lowerCode = code.toLowerCase();

  if (lowerCode === 'others') {
    return localizedOthersName || fallbackText;
  }

  const candidateCodes = Array.from(
    new Set([code, lowerCode, code.toUpperCase()].filter(Boolean)),
  );

  for (const candidateCode of candidateCodes) {
    const countryName = getCountryNameByCountryCode(regionsCountries, candidateCode);
    if (countryName && countryName !== candidateCode) {
      return countryName;
    }
  }

  return fallbackText;
};

const getIsOthersChartName = (...names: any[]) => {
  return names.some((name) => {
    if (typeof name !== 'string') {
      return false;
    }

    return name.trim().toLowerCase() === 'others';
  });
};

export const getChartDisableType = (params: {
  hasDeviceBtn?: boolean;
  disableChartType: string[];
  isCountriesPage?: boolean;
}) => {
  const { hasDeviceBtn, disableChartType, isCountriesPage } = params;
  // 默认配置写在配置项里
  if (isCountriesPage || hasDeviceBtn) {
    return disableChartType;
  }
  // Stats如果没有ByDevice,则只展示折线图
  return [ChartType.Bar, ChartType.StackedLine, ChartType.Pie];
};

export const getHasDeviceBtn = (params: {
  chartType?: string;
  metricHasDeviceBtn: boolean;
  isCountriesPage?: boolean;
  entityType: string;
}) => {
  const { chartType, metricHasDeviceBtn, isCountriesPage, entityType } = params;
  if (entityType !== PC_CONSOLE_COMBINE_ENTITY_TYPE) {
    return false;
  }
  // Countries指标只有柱状图有bydevice
  if (isCountriesPage) {
    return metricHasDeviceBtn && chartType === ChartType.Bar;
  }
  return metricHasDeviceBtn;
};

export const getDefaultPageSortAttr = (
  isStatsPage: boolean,
  sortValue: string,
  sortMetric: string,
) => {
  const pageSortAttr = isStatsPage
    ? {
        currentSortValue: 'desc',
        currentSortMetric: 'date',
        currentRatioType: 0,
      }
    : {
        currentSortValue: sortValue,
        currentSortMetric: sortMetric,
        currentRatioType: 0,
      };
  return pageSortAttr;
};

export const lineSeriesFormatter = (params: {
  list: any;
  legendMetric: string;
  isPercentLine?: boolean;
  earliestClickValue: string;
  dateType: string;
}) => {
  const {
    list,
    legendMetric,
    isPercentLine = false,
    earliestClickValue,
    dateType,
  } = params || {};

  if (!Array.isArray(list) || list?.length < 1) return [];
  let arr: any = [];
  const listTemp = list.sort(
    (a: any, b: any) => moment(a?.date) - moment(b?.date),
  );

  // eslint-disable-next-line no-restricted-syntax
  for (const i in listTemp) {
    const lineNum = listTemp[i].metrics
      ? listTemp[i].metrics?.find((item: any) => {
          const _metric = item?.metric || item?.name;
          return _metric == legendMetric;
        })?.value // Stats折线
      : listTemp[i]?.value; // Coutries折线
    arr.push({
      time: showTimeByDateType(listTemp[i].date, dateType?.toLocaleLowerCase()),
      value: isPercentLine ? lineNum * 100 : lineNum,
    });
  }
  if (earliestClickValue != '') {
    // eslint-disable-next-line no-restricted-syntax
    for (const n in arr) {
      if (arr[n]) {
        if (arr[n].time === earliestClickValue) {
          arr = arr.slice(n);
        }
      }
    }
  }
  return arr;
};

export const stackLineSeriesFormatter = (params: {
  list: any;
  legendMetric: string;
  isPercentLine?: boolean;
  earliestClickValue: string;
  dateType: string;
}) => {
  const {
    list,
    legendMetric,
    isPercentLine = false,
    earliestClickValue,
    dateType,
  } = params || {};

  let arr: any[] = [];
  if (!Array.isArray(list)) {
    return [];
  }

  let keys: string[] = [];
  list.forEach((element) => {
    if (element.metrics) {
      keys = [...keys, ...Object.keys(element.metrics)];
    }
  });
  const storeKeys = [...new Set(keys)]?.filter(
    (t) => t?.toLocaleLowerCase() !== 'all',
  );
  // const storeKeys = Object.keys(list?.[0]?.metrics);
  storeKeys?.forEach((store: string) => {
    const storeData: any = {
      id: deviceValueTextMap[store] || store,
      name: deviceValueTextMap[store] || store,
      history: [],
    };
    list?.forEach((item: any) => {
      // if (item?.metrics[store] && Array.isArray(item?.metrics[store])) {
      //   const lineNum = item?.metrics[store]?.find((metricItem: any) => {
      //     const _metric = metricItem?.metric || metricItem?.name;
      //     return _metric == legendMetric;
      //   })?.value;
      //   if (lineNum) {
      //     storeData.history?.push({
      //       time: showTimeByDateType(item?.date, dateType?.toLocaleLowerCase()),
      //       value: isPercentLine ? lineNum * 100 : lineNum,
      //     });
      //   }
      // }

      const metricData = item?.metrics?.[store]?.find((metricItem: any) => {
        const _metric = metricItem?.metric || metricItem?.name;
        return _metric === legendMetric;
      });
      const metricValue = Number.parseFloat(metricData?.value);
      storeData?.history?.push({
        time: showTimeByDateType(item?.date, dateType?.toLocaleLowerCase()),
        value: Number.isNaN(metricValue)
          ? ''
          : isPercentLine
          ? metricValue * 100
          : metricValue,
      });
    });
    arr.push(storeData);
  });

  if (earliestClickValue != '') {
    for (const n in arr) {
      if (arr[n]) {
        if (arr[n].time == earliestClickValue) {
          arr = arr.slice(n);
        }
      }
    }
  }
  return arr;
};

export const commonStackLineSeriesFormatter = (params: {
  list: any;
  legendMetric: string;
  isPercentLine?: boolean;
  earliestClickValue: string;
  dateType: string;
}) => {
  const {
    list,
    legendMetric,
    isPercentLine = false,
    earliestClickValue,
    dateType,
  } = params || {};

  let arr: any[] = [];
  const storeKeys = Object.keys(list);
  storeKeys.forEach((store: string) => {
    const storeData: any = {
      id: deviceValueTextMap[store] || store,
      name: deviceValueTextMap[store] || store,
      history: [],
    };
    const timeList = list?.[store];
    timeList?.forEach((item: any) => {
      const lineNum = item?.metrics?.find((metricItem: any) => {
        const _metric = metricItem?.metric || metricItem?.name;
        return _metric == legendMetric;
      })?.value;
      storeData.history.push({
        time: showTimeByDateType(item?.date, dateType?.toLocaleLowerCase()),
        value: isPercentLine ? lineNum * 100 : lineNum,
      });
    });
    arr.push(storeData);
  });

  if (earliestClickValue != '') {
    for (const n in arr) {
      if (arr[n]) {
        if (arr[n].time == earliestClickValue) {
          arr = arr.slice(n);
        }
      }
    }
  }
  return arr;
};
export const countriesBarSeriesFormatter = (params: {
  barData: any[];
  legendMetric: string;
  regionsCountries: any[];
  localizedOthersName?: string;
}) => {
  const { barData, legendMetric, regionsCountries, localizedOthersName } = params;
  return barData?.map((item: any, i: number): any => {
    let barNum = item.metrics
      ? item.metrics?.find((item: any) => {
          const _metric = item?.name || item?.metric;
          return _metric == legendMetric;
        })?.value
      : 0;
    if (getIfPercentage(legendMetric)) {
      barNum = barNum * 100;
    }
    const nameStr = item?.name || item?.market || '';
    const countryName = getLocalizedCountryChartName({
      regionsCountries,
      countryCode: item?.market,
      fallbackName: nameStr,
      localizedOthersName,
    });
    return {
      type: 'bar',
      name: countryName,
      value: barNum,
      market: item.market,
      itemStyle: {
        color: ECHARTSCOLOR[i],
      },
      label: {
        show: true,
        position: 'top',
        color: '#444',
        distance: 4,
        fontSize: 12,
        formatter: (params: any) =>
          `${
            newMathFormat(Number(params.value), true) +
            (getIfPercentage(legendMetric) ? '%' : '')
          }`,
      },
    };
  });
};

export const digitalPieSeriesFormatter = (params: {
  list: any[];
  regionsCountries: any[];
  legendMetric: string;
  enableCountryNameLocale?: boolean;
  localizedOthersName?: string;
}) => {
  const {
    list,
    regionsCountries,
    legendMetric,
    enableCountryNameLocale = false,
    localizedOthersName,
  } = params;
  const pieList: any = [];
  list?.forEach((item: any) => {
    const metricNum = item.metrics
      ? item.metrics?.find((item: any) => item?.metric == legendMetric)?.value
      : 0;
    const nameStr = item?.name || item?.market || '';
    let countryName = getCountryNameByCountryCode(regionsCountries, nameStr);
    const isOthers = getIsOthersChartName(item?.market, item?.name);
    if (enableCountryNameLocale || isOthers) {
      countryName = getLocalizedCountryChartName({
        regionsCountries,
        countryCode: item?.market,
        fallbackName: nameStr,
        localizedOthersName,
      });
    }
    const objTemp = {
      app_id: item.market,
      name: countryName,
      value: metricNum,
    };
    pieList.push(objTemp);
  });
  return pieList;
};

export const pieSeriesFormatter = (params: {
  list: any[];
  regionsCountries: any[];
  legendMetric: string;
  calOtherList: any;
  allData: any[];
  enableCountryNameLocale?: boolean;
  localizedOthersName?: string;
}) => {
  const {
    list,
    regionsCountries,
    legendMetric,
    calOtherList,
    allData,
    enableCountryNameLocale = false,
    localizedOthersName,
  } = params;

  let totalValue = 0;

  if (Array.isArray(allData) && Array.isArray(calOtherList)) {
    allData?.forEach((item: any) => {
      let metricNum = 0;
      if (item.metrics && Array.isArray(item.metrics)) {
        const metricObj = item.metrics?.find(
          (subItem: any) => subItem.metric == legendMetric,
        );
        if (metricObj) {
          metricNum = metricObj.value || 0;
        }
      }
      totalValue += metricNum;
    });
  } else {
    // 获取当前 legendMetric 对应的总值
    totalValue = calOtherList?.metrics
      ? calOtherList?.metrics?.find((item: any) => {
        const metricVal = item.name || item.metric;
        return metricVal === legendMetric;
      })?.value
      : 0;
  }

  const pieList: any = [];
  let sum = 0;
  list?.forEach((item: any) => {
    const metricNum = item.metrics
      ? item.metrics?.find((item: any) => {
        const metricVal = item.name || item.metric;
        return metricVal === legendMetric;
      })?.value
      : 0;
    let countryName = item.name;
    if (item.market) {
      countryName = getCountryNameByCountryCode(regionsCountries, item.market);
    }
    const isOthers = getIsOthersChartName(item?.market, item?.name);
    if (enableCountryNameLocale || isOthers) {
      countryName = getLocalizedCountryChartName({
        regionsCountries,
        countryCode: item?.market,
        fallbackName: item?.name,
        localizedOthersName,
      });
    }
    const objTemp = {
      app_id: item.market,
      name: countryName,
      value: metricNum,
    };
    sum += metricNum;
    pieList.push(objTemp);
  });

  if (Array.isArray(allData) && allData?.length > 5) {
    // Others = 后端返回的 - top5总和
    const otherVal = list?.length === 0 ? 0 : totalValue - sum;
    if (otherVal > 0) {
      pieList.push({
        value: otherVal,
        name: localizedOthersName || 'Others',
      });
    }
  }
  return pieList;
};

export const countriesPCULineSeriesFormatter = (
  params: {
    list: any;
    legendMetric: string;
    isPercentLine?: boolean;
    earliestClickValue: string;
    dateType: string;
  },
  segementList: any[],
) => {
  const {
    list,
    legendMetric,
    isPercentLine = false,
    earliestClickValue,
    dateType,
  } = params || {};
  const arrGameData: any = [];

  segementList?.forEach((opt: any) => {
    for (const n in list) {
      const gameObj = list[n];
      const objTemp = {
        id: opt?.value,
        name: opt?.textStr || opt?.text,
        history: lineSeriesFormatter({
          list: gameObj.time_line || [],
          legendMetric: opt?.value,
          // legendMetric,
          isPercentLine,
          earliestClickValue,
          dateType,
        }),
      };
      arrGameData.push(objTemp);
    }
  });
  const temp = {
    source: Array.isArray(arrGameData) ? arrGameData : [],
  };
  return temp;
};

export const countriesPCUBarSeriesFormatter = (
  params: {
    barData: any[];
    legendMetric: string;
    regionsCountries: any[];
  },
  segementList: any[],
) => {
  const { barData, legendMetric, regionsCountries } = params;
  const tmpList: any[] = [];
  segementList?.forEach((opt) => {
    const option = countriesBarSeriesFormatter({
      barData,
      legendMetric: opt?.value,
      regionsCountries,
    });
    tmpList.push(option?.[0]);
  });
  return tmpList;
};
/*
//处理折线图数据拿到有数据的2个指标值作为可视化指标默认值
export const getDefaultHasDataValues = (sortMetricsOptions: any[], lineData: any, getMetricValueOfCvm: Function) => {
  const defaultselectValues = sortMetricsOptions?.map(t => t?.value)?.slice(0, 2) || [];
  try {
    if (sortMetricsOptions?.length <= 2) {
      return defaultselectValues
    }
    let hasDatatMetricsValue = [];
    for (let metricIdx = 0; metricIdx < sortMetricsOptions?.length; metricIdx++) {
      const opt = sortMetricsOptions[metricIdx];
      for (let lineInx = 0; lineInx < lineData?.length; lineInx++) {
        const metricObj = getMetricValueOfCvm?.(lineData[lineInx], opt?.value);
        if (metricObj?.value) {
          hasDatatMetricsValue.push(sortMetricsOptions[metricIdx]);
          break;
        }
      }
      if (hasDatatMetricsValue.length === 2) {
        break;
      }
    }
    if (!hasDatatMetricsValue?.length) {
      return defaultselectValues
    } else if (hasDatatMetricsValue?.length === 1) {
      //只有一个有数据时, 也要返回2个值
      const metricValue = hasDatatMetricsValue[0]?.value;
      if (defaultselectValues?.includes(metricValue)) {
        return defaultselectValues;
      } else {
        return [defaultselectValues[0], metricValue,]
      }
    }
    return hasDatatMetricsValue?.map(t => t?.value)
  } catch (err) {
    console.log(err)
    return defaultselectValues
  }
}
*/
// 处理折线图数据拿到有数据的所有指标值，其他的隐藏掉
export const getHasDataValues = (
  sortMetricsOptions: any[],
  lineData: any,
  getMetricValueOfCvm: (cvm: any, metricKey: string) => any,
) => {
  const hasDatatMetricsValue = [];
  try {
    for (
      let metricIdx = 0;
      metricIdx < sortMetricsOptions?.length;
      metricIdx++
    ) {
      const opt = sortMetricsOptions[metricIdx];
      for (let lineInx = 0; lineInx < lineData?.length; lineInx++) {
        const hasData = isMultiValueMetric(opt?.value)
          ? hasMultiValueMetricDataInRow(
              lineData[lineInx],
              opt?.value,
              getMetricValueOfCvm,
            )
          : Boolean(getMetricValueOfCvm?.(lineData[lineInx], opt?.value)?.value);
        if (hasData) {
          hasDatatMetricsValue.push(sortMetricsOptions[metricIdx]);
          break;
        }
      }
    }
    return hasDatatMetricsValue;
  } catch (err) {
    console.log(err);
    return sortMetricsOptions;
  }
};

// 暂时固定几个游戏为月粒度
const MONTHGAMEID = [
  'c00002466',
  'c00038686',
  'c0f1633bea146839295800b7cd4cadc58',
  'c00019859',
  'c00076892',
];

export const getIsFortyPage = (
  pageId: string,
  entityType: string,
  isFortyDay: boolean,
  gameId = '',
) => {
  const justifyPage = [
    DETAIL_PAGEID.overview,
    DETAIL_PAGEID.pcu,
    DETAIL_PAGEID.digitalRevenue,
    DETAIL_PAGEID.steamData,
  ].includes(pageId);
  const fortyPage = ['pc', PC_CONSOLE_COMBINE_ENTITY_TYPE]?.includes(
    entityType,
  ); // ? basicInfo?.is_forty_days
  // 未发行的游戏，因暂时未确定有数据的时间粒度是什么，暂时固定几个游戏为月粒度
  if (MONTHGAMEID.includes(gameId)) {
    return false;
  }
  if (justifyPage && fortyPage && isFortyDay) {
    return true;
  }
  return false;
};
